import { clearState } from "core/store/enhancers/clearStateEnhance";
import { IRequestAuth } from "core/api";
import { historyPushRequest } from "../history";
import {
  getReviewers,
  getReviewer,
  IGetReviewersResponse,
  IGetReviewerResponse,
  ICreateTechniciansResponse,
  IGetTechniciansResponse,
  IRemoveTechnicianResponse,
  resetPassword,
  createTechnicians,
  getTechnicians,
  getTechnicianPhysicians,
  getRegistrationCodeValidity,
  removeTechnician,
} from "core/api/study_reviewer";
import { startSubmit, stopSubmit, reset } from "redux-form";
import { delay } from "redux-saga";
import {
  all,
  call,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
} from "redux-saga/effects";
import * as Features from "study_reviewer/features";
import * as routes from "../../routes";
import { actions as mainActions } from "../main";
import {
  clearTechnicians,
  IActionInviteTechniciansRequest,
  IActionGetTechniciansRequest,
  IActionRemoveTechnicianRequest,
  resetIsFetchingTechnicians,
  setIsFetchingTechnicians,
  setTechnicians,
  addTechnicians,
  removeTechnician as removeTechnicianAction,
  IActionGetRegistrationCodeValidityRequest,
  setIsRegistrationCodeValid,
} from "./actions";
import * as actions from "./actionTypes";

/**
 * Dependencies needed for the sagas
 */
export interface IDependencies extends IMainSagaDependencies {}

/**
 * Dependencies that need to be injected into the main saga
 */
export interface IMainSagaDependencies {
  withAuth: (fn: (...args: any[]) => any) => void;
  withTokenAuth: (fn: (auth: IRequestAuth, ...args: any[]) => any) => void;
  onTokenExpired: () => void;
  redirectTo: (path: string) => void;
}

export function* inviteTechniciansRequest(
  dependencies: IDependencies,
  action: IActionInviteTechniciansRequest
) {
  yield put(startSubmit(action.form));
  try {
    const { reviewerId, ...rest } = action.payload;
    const response = yield call(
      dependencies.withAuth,
      createTechnicians,
      reviewerId,
      rest
    );
    if (response.status === 200) {
      const { technicians } = response.data;
      yield put(addTechnicians(technicians));
    } else if (response.status === 401) {
      yield call(dependencies.onTokenExpired);
    } else {
      // TODO I18N
      const error = response.data.message;
      yield put(stopSubmit(action.form, { _error: error }));
    }
  } catch (e) {
    // TODO I18N
    yield put(
      mainActions.snackbarSetRequest(
        "Network error, please check your connection and try again."
      )
    );
    return;
  }
}

export function* getTechniciansRequest(
  dependencies: IDependencies,
  action: IActionGetTechniciansRequest
) {
  yield put(setIsFetchingTechnicians());
  let response: IGetTechniciansResponse;
  try {
    response = yield call(
      dependencies.withAuth,
      getTechnicians,
      action.payload
    );
    if (response.status === 200) {
      yield put(clearTechnicians());
      const { technicians } = response.data;
      yield put(addTechnicians(technicians));
    } else if (response.status === 401) {
      yield call(dependencies.onTokenExpired);
    } else {
      // TODO I18N
      yield put(
        mainActions.snackbarSetRequest(
          "Oops! Something went wrong. Please try again later."
        )
      );
    }
  } catch (e) {
    // TODO I18N
    yield put(
      mainActions.snackbarSetRequest(
        "Network error, please check your connection and try again."
      )
    );
    yield put(resetIsFetchingTechnicians());
    return;
  }
  yield put(resetIsFetchingTechnicians());
}

export function* removeTechnicianRequest(
  dependencies: IDependencies,
  action: IActionRemoveTechnicianRequest
) {
  yield put(setIsFetchingTechnicians());
  let response: IRemoveTechnicianResponse;
  try {
    response = yield call(
      dependencies.withAuth,
      removeTechnician,
      action.payload.reviewerId,
      action.payload.technicianId
    );
    if (response.status === 200) {
      const { technician } = response.data;
      yield put(removeTechnicianAction(technician));
    } else if (response.status === 401) {
      yield call(dependencies.onTokenExpired);
    } else {
      // TODO I18N
      yield put(
        mainActions.snackbarSetRequest(
          "Oops! Something went wrong. Please try again later."
        )
      );
    }
  } catch (e) {
    // TODO I18N
    yield put(
      mainActions.snackbarSetRequest(
        "Network error, please check your connection and try again."
      )
    );
    yield put(resetIsFetchingTechnicians());
    return;
  }
  yield put(resetIsFetchingTechnicians());
}

export function* getRegistrationCodeValidityRequest(
  action: IActionGetRegistrationCodeValidityRequest
) {
  yield put(setIsFetchingTechnicians());
  try {
    const response = yield call(getRegistrationCodeValidity, action.payload);
    if (response.status === 200) {
      const { isValid } = response.data;
      yield put(setIsRegistrationCodeValid(isValid));
    } else {
      // TODO I18N
      yield put(
        mainActions.snackbarSetRequest(
          "Oops! Something went wrong. Please try again later."
        )
      );
    }
  } catch (e) {
    // TODO I18N
    yield put(
      mainActions.snackbarSetRequest(
        "Network error, please check your connection and try again."
      )
    );
    yield put(setIsFetchingTechnicians());
    return;
  }
  yield put(setIsFetchingTechnicians());
}

function* mainSaga(dependencies: IMainSagaDependencies) {
  const sagaDependencies = {
    ...dependencies,
  };
  yield all([
    takeLatest<IActionInviteTechniciansRequest, IDependencies>(
      actions.INVITE_TECHNICIANS,
      inviteTechniciansRequest,
      sagaDependencies
    ),
    takeLatest<IActionGetTechniciansRequest, IDependencies>(
      actions.GET_TECHNICIANS_REQUEST,
      getTechniciansRequest,
      sagaDependencies
    ),
    takeLatest<IActionRemoveTechnicianRequest, IDependencies>(
      actions.REMOVE_TECHNICIAN_REQUEST,
      removeTechnicianRequest,
      sagaDependencies
    ),
    takeLatest(
      actions.GET_IS_REGISTRATION_CODE_VALID_REQUEST,
      getRegistrationCodeValidityRequest
    ),
  ]);
}

function* saga(dependencies: IMainSagaDependencies) {
  yield all([fork<IMainSagaDependencies>(mainSaga, dependencies)]);
}

export default saga;



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./study_reviewer/src/store/technicians/saga.ts